import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Divider,
} from "@mui/material";
import { getSOWResourcingDetails, updateSOWResourcingDetails } from "../../http/requests/sow";
import { toast } from "react-toastify";
import { toastOptions } from "../../common/utils/toasterOptions";
import { tableColumnsData } from "../../common/utils/datatable";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const formatDate = (d) => {
  if (!d) return null;
  if (typeof d === "string") return d.split("T")[0] || d;
  if (d.format) return d.format("YYYY-MM-DD");
  return d;
};

const generateMonths = (startDate, endDate) => {
  const start = startDate ? new Date(formatDate(startDate)) : null;
  const end = endDate ? new Date(formatDate(endDate)) : null;
  if (!start || !end || start > end) return [];
  const rowsGenerated = [];
  let current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endLastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0);
  while (current <= endLastDay) {
    const monthName = MONTH_NAMES[current.getMonth()];
    const year = current.getFullYear();
    rowsGenerated.push({
      month: monthName,
      year: String(year),
      billableResourceCount: "",
      nonBillableResourceCount: "",
      shadowResourceCount: "",
    });
    current.setMonth(current.getMonth() + 1);
  }
  return rowsGenerated;
};

const SOW_HIDDEN_RESOURCING_COLUMNS = ["nonBillableResourceCount", "shadowResourceCount"];

const SOWResourcingDetailsModal = ({ open, onClose, sowId, sowName, sowStartDate, sowEndDate, onSuccess }) => {
  const allColumns = tableColumnsData["ResourcingDetailsProjectAdmin"] || [];
  const columns = allColumns.filter((col) => !SOW_HIDDEN_RESOURCING_COLUMNS.includes(col.id));
  const [rows, setRows] = useState([]);
  const [newRowAdded, setNewRowAdded] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const inputFieldHandler = (event, key) => {
    const keyArray = key.split("-");
    if (keyArray.length !== 3) return;
    const valuesArray = event.target.value.split(".");
    if (valuesArray.length > 1 && valuesArray[1] && valuesArray[1].length >= 3) {
      toast.info("You cannot enter values after 2 decimal places", toastOptions);
      return;
    }
    if (event.target.value !== "" && isNaN(event.target.value)) {
      toast.info("Please enter numbers only", toastOptions);
      return;
    }
    setNewRowAdded((prev) => ({ ...prev, [key]: event.target.value }));
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.month === keyArray[0] && row.year === keyArray[1]
          ? { ...row, [keyArray[2]]: event.target.value }
          : row
      )
    );
  };

  const createInputField = (col, row) => {
    const key = `${row.month}-${row.year}-${col.id}`;
    return (
      <TableCell key={key} style={{ maxWidth: col.maxWidth || "auto" }}>
        <TextField
          fullWidth
          size="small"
          variant="standard"
          inputProps={{
            maxLength: 100,
            min: col.min === 0 ? col.min : undefined,
            max: col.max || undefined,
            style: { fontSize: "1rem" },
          }}
          type={col.fieldType || "number"}
          placeholder={col.label}
          value={newRowAdded[key] ?? row[col.id] ?? ""}
          onChange={(e) => inputFieldHandler(e, key)}
          disabled={col.disabled}
        />
      </TableCell>
    );
  };

  useEffect(() => {
    if (!open || !sowId) return;
    const start = formatDate(sowStartDate);
    const end = formatDate(sowEndDate);
    const generated = generateMonths(sowStartDate, sowEndDate);
    setRows(generated);
    setNewRowAdded({});
    setLoading(true);
    getSOWResourcingDetails(sowId)
      .then((res) => {
        const data = res?.data ?? res ?? [];
        const list = Array.isArray(data) ? data : [];
        setRows((prev) =>
          prev.map((row) => {
            const match = list.find((d) => String(d.month ?? d.Month) === row.month && String(d.year ?? d.Year) === row.year);
            if (match) {
              return {
                ...row,
                billableResourceCount: match.billableResourceCount ?? match.BillableResourceCount ?? "",
                nonBillableResourceCount: match.nonBillableResourceCount ?? match.NonBillableResourceCount ?? "",
                shadowResourceCount: match.shadowResourceCount ?? match.ShadowResourceCount ?? "",
              };
            }
            return row;
          })
        );
      })
      .catch(() => setRows(generated))
      .finally(() => setLoading(false));
  }, [open, sowId, sowStartDate, sowEndDate]);

  const handleUpdateDetails = async () => {
    if (!sowId) return;
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        sowId,
        year: String(r.year),
        month: r.month,
        billableResourceCount: r.billableResourceCount === "" || r.billableResourceCount == null ? null : Number(r.billableResourceCount),
        nonBillableResourceCount: r.nonBillableResourceCount === "" || r.nonBillableResourceCount == null ? null : Number(r.nonBillableResourceCount),
        shadowResourceCount: r.shadowResourceCount === "" || r.shadowResourceCount == null ? null : Number(r.shadowResourceCount),
      }));
      await updateSOWResourcingDetails(sowId, payload);
      toast.success("Monthly projection updated successfully", toastOptions);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to update monthly projection", toastOptions);
    } finally {
      setSaving(false);
    }
  };

  const extractDate = (datetime) => {
    if (!datetime) return "";
    const s = typeof datetime === "string" ? datetime : datetime.format ? datetime.format("YYYY-MM-DD") : String(datetime);
    return s.slice(0, 10) || s;
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={false} PaperProps={{ sx: { borderRadius: 2, minHeight: "60vh", maxWidth: 480 } }}>
      <DialogTitle sx={{ color: "#1773bc", fontWeight: 600, pb: 0 }}>
        SOW Monthly Projection
      </DialogTitle>
      <Divider sx={{ borderColor: "#1773bc", opacity: 0.5 }} />
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <div style={{ fontWeight: "bold", fontSize: "1.25rem", color: "#1773BC", marginBottom: "0.75rem" }}>
            SOW Details
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}>
            <div>
              <div style={{ fontSize: "0.875rem", color: "#6c757d", marginBottom: "0.25rem" }}>SOW Name</div>
              <div style={{ fontSize: "1rem", fontWeight: 600 }}>{sowName || "—"}</div>
            </div>
            <div>
              <div style={{ fontSize: "0.875rem", color: "#6c757d", marginBottom: "0.25rem" }}>SOW Start Date</div>
              <div style={{ fontSize: "1rem" }}>{extractDate(sowStartDate) || "—"}</div>
            </div>
            <div>
              <div style={{ fontSize: "0.875rem", color: "#6c757d", marginBottom: "0.25rem" }}>SOW End Date</div>
              <div style={{ fontSize: "1rem" }}>{extractDate(sowEndDate) || "—"}</div>
            </div>
          </div>
        </Box>
        <div style={{ fontWeight: "bold", fontSize: "1.25rem", color: "#1773BC", marginBottom: "0.75rem" }}>
          Resource Allocation Details
        </div>
        {loading ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>Loading...</Box>
        ) : (
          <TableContainer sx={{ maxHeight: "32rem" }} component={Paper}>
            <Table size="small" stickyHeader aria-label="sow resourcing table">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f8fc" }}>
                  {columns.map((col) => (
                    <TableCell key={col.id} align="left" sx={{ color: "#3a75c2", fontWeight: 600 }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={`${row.year}-${row.month}-${idx}`}>
                    {columns.map((col) =>
                      col.id === "month" || col.id === "year" ? (
                        <TableCell key={col.id}>{row[col.id]}</TableCell>
                      ) : (
                        createInputField(col, row)
                      )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={handleUpdateDetails} disabled={saving || loading} sx={{ backgroundColor: "#1773bc" }}>
          {saving ? "Updating..." : "Update Details"}
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SOWResourcingDetailsModal;
